import React, { useEffect } from 'react';
import { cn } from '../../utils/cn';
import { useI18n } from '../../i18n/I18nContext';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

const sizes: Record<ModalSize, string> = {
  sm: 'sm:max-w-lg',
  md: 'sm:max-w-xl',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-3xl',
};

/**
 * Full-screen sheet on phones, centred dialog from `sm` up.
 * The body is the only scrolling region so the header stays reachable
 * on short mobile viewports.
 */
export function Modal({
  open,
  title,
  children,
  onClose,
  size = 'sm',
  wide,
}: {
  open: boolean;
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  size?: ModalSize;
  /** Legacy alias kept for call sites that only need "wider than default". */
  wide?: boolean;
}) {
  const { t } = useI18n();

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] !mt-0">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="absolute inset-0 flex items-end justify-center sm:items-center sm:p-4">
        <div
          className={cn(
            'flex max-h-[92dvh] w-full flex-col overflow-hidden border border-slate-200 bg-white shadow-xl',
            'rounded-t-2xl sm:max-h-[90dvh] sm:rounded-2xl dark:border-slate-700 dark:bg-slate-900',
            sizes[wide ? 'lg' : size]
          )}
        >
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 sm:px-5 sm:py-4 dark:border-slate-700">
            <div className="min-w-0 truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
              {title}
            </div>
            <button
              className="-mr-1 shrink-0 rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
              onClick={onClose}
              type="button"
              aria-label={t('common.aria.closeModal')}
            >
              ✕
            </button>
          </div>
          <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-5 sm:pb-4">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
